import Button from "@modelve/ui/components/button";
import Modal from "@modelve/ui/components/modal";
import { useApiDeleteAsync } from "api/hooks";
import { ClientType } from "api/utils";
import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type DeleteStatus = "deleted" | "dismissed";

export interface DeleteModalContextState {
  request?: {
    title: string;
    message?: string;
    route: string;
    clientType: ClientType;
    onCompleted: (status: DeleteStatus) => void;
    onError: (error: unknown) => void;
  };
  status?: DeleteStatus;
  setRequest: (request: DeleteModalContextState["request"]) => void;
}

const DeleteModalContext = createContext<DeleteModalContextState>({
  setRequest: () => {},
});

export const DeleteModelProvider = ({ children }: { children: ReactNode }) => {
  const [request, setRequest] = useState<DeleteModalContextState["request"]>();

  const context = useMemo<DeleteModalContextState>(
    () => ({
      request: request,
      setRequest: setRequest,
    }),
    [request, setRequest],
  );

  return (
    <DeleteModalContext.Provider value={context}>
      <DeleteModal />
      {children}
    </DeleteModalContext.Provider>
  );
};

const useDeleteModalContext = () =>
  useContext<DeleteModalContextState>(DeleteModalContext);

export const DeleteModal = () => {
  const { t } = useTranslation();
  const { request, setRequest } = useDeleteModalContext();

  const { isLoading, onRequest } = useApiDeleteAsync(
    request?.clientType ?? ClientType.Base,
  );

  const onDelete = async () => {
    if (!request) return;
    try {
      await onRequest(request.route);
      request.onCompleted("deleted");
    } catch (error) {
      request.onError(error);
    } finally {
      setRequest(undefined);
    }
  };

  const onDismiss = () => {
    request!.onCompleted("dismissed");
    setRequest(undefined);
  };

  return (
    <Modal show={!!request}>
      <Modal.Header>
        <Modal.Title>{request?.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {request?.message ??
          t(
            "common.message.confirm_delete",
            "This action is final and can not be reversed.",
          )}
      </Modal.Body>
      <Modal.Footer className="flex flex-row justify-between">
        <Button
          disabled={isLoading}
          variant="danger"
          onClick={onDelete}
          size="sm"
        >
          {t("common.button.delete", "Delete")}
        </Button>
        <Button disabled={isLoading} onClick={onDismiss} size="sm">
          {t("common.button.cancel", "Cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const useDeleteModal = () => {
  const { setRequest } = useDeleteModalContext();

  const toggle = (
    title: string,
    route: string,
    clientType = ClientType.Base,
    message?: string,
  ) =>
    new Promise<DeleteStatus>((res, rej) => {
      setRequest({
        title,
        route,
        clientType,
        message,
        onCompleted: res,
        onError: rej,
      });
    });

  return {
    toggle,
  };
};
